import React from "react";
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
} from "@mui/material";
import { FlexRow, Spacer } from "../../../components";
import { Button as Btn } from "../../../components/atoms/Button";
import { useSnackbar } from "../../../context/SnackbarContext";
import theme from "../../../theme";
import type { ColorsCodeType } from "../../../typings";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import api from "../../../api/axiosInstance";
import { Mentor } from "../../../typings/scholarship-applications";

interface AssignMentorModalProps {
  open: boolean;
  onClose: () => void;
  scholarshipApplicationId: number;
  onRefresh: () => void;
}

interface FormValues {
  mentor_id: number | "";
}

const validationSchema = Yup.object({
  mentor_id: Yup.number().required("Please select a mentor"),
});

export default function AssignMentorModal({
  open,
  onClose,
  scholarshipApplicationId,
  onRefresh,
}: AssignMentorModalProps) {
  const { showSnackbar } = useSnackbar();
  const [mentors, setMentors] = useState<Mentor[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchingMentors, setFetchingMentors] = useState(false);

  const initialValues: FormValues = {
    mentor_id: "",
  };

  useEffect(() => {
    if (open) {
      fetchMentors();
    }
  }, [open]);

  const fetchMentors = async () => {
    setFetchingMentors(true);
    try {
      const response = await api.get("/users?role=MENTOR");
      setMentors(response.data.data);
    } catch (err) {
      console.error("Failed to fetch mentors:", err);
      showSnackbar("Failed to load mentors. Please try again.", "error");
    } finally {
      setFetchingMentors(false);
    }
  };

  const handleAssignMentor = async (values: FormValues) => {
    setLoading(true);
    try {
      const payload = {
        scholarship_application_id: scholarshipApplicationId,
        mentor_id: values.mentor_id as number,
      };

      console.log("assign mentor payload", payload);

      const response = await api.post(
        "/scholarship-applications/assign-mentor/",
        payload
      );

      console.log("assign mentor response ==>", response.data.data);

      showSnackbar("Mentor assigned successfully!", "success");
      
      // Refresh the applications list
      onRefresh();
      onClose();
    } catch (err) {
      console.error("Failed to assign mentor:", err);
      showSnackbar("Failed to assign mentor. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };



  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        style: {
          borderRadius: "6px",
          maxWidth: "468px",
          width: "100%",
        },
      }}
    >
      <DialogTitle
        style={{
          padding: "24px 24px 0",
          fontSize: "24px",
          fontWeight: 600,
          color: theme.palette.colors.black[800] as string,
        }}
      >
        Assign Mentor
      </DialogTitle>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleAssignMentor}
      >
        {({ values, setFieldValue, errors, touched, isValid, dirty }) => (
          <Form>
            <DialogContent style={{ padding: "16px 24px" }}>
              <Typography
                variant="body2"
                style={{
                  marginBottom: "16px",
                  color: theme.palette.colors.black[600] as string,
                }}
              >
                Select a mentor to assign to this scholarship application.
              </Typography>

              <FormControl
                fullWidth
                error={touched.mentor_id && Boolean(errors.mentor_id)}
                style={{ marginBottom: "15px" }}
              >
                <Autocomplete
                  options={mentors}
                  getOptionLabel={(option) => option.name}
                  value={
                    mentors.find((mentor) => mentor.id === values.mentor_id) ||
                    null
                  }
                  onChange={(event, newValue) => {
                    setFieldValue("mentor_id", newValue ? newValue.id : "");
                  }}
                  loading={fetchingMentors}
                  disabled={fetchingMentors}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Mentor"
                      error={touched.mentor_id && Boolean(errors.mentor_id)}
                      helperText={
                        touched.mentor_id && errors.mentor_id
                          ? (errors.mentor_id as string)
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {fetchingMentors ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                      overflow: "auto",
                    },
                  }}
                  filterOptions={(options, { inputValue }) =>
                    options.filter((option) =>
                      option.name
                        .toLowerCase()
                        .includes(inputValue.toLowerCase())
                    )
                  }
                  noOptionsText={
                    fetchingMentors ? "Loading..." : "No mentors found"
                  }
                />
              </FormControl>
            </DialogContent>

            <FlexRow
              justify="flex-end"
              align="center"
              style={{ padding: "5px 24px 16px" }}
            >
              <Btn
                buttonColor={theme.palette.colors.black[600] as ColorsCodeType}
                textColor={theme.palette.colors.white[400] as ColorsCodeType}
                onClick={onClose}
                type="button"
                disabled={loading}
              >
                Cancel
              </Btn>
              <Spacer horizontal={1} />
              <Btn
                type="submit"
                disabled={!(isValid && dirty) || loading}
                buttonColor={theme.palette.colors.green[700] as ColorsCodeType}
                textColor={theme.palette.colors.white[400] as ColorsCodeType}
              >
                {loading ? (
                  <>
                    <CircularProgress
                      size={16}
                      style={{
                        marginRight: 8,
                        color: theme.palette.colors.white[400] as string,
                      }}
                    />
                    Assigning...
                  </>
                ) : (
                  "Assign Mentor"
                )}
              </Btn>
            </FlexRow>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
} 